import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import FloatingInput from "../common/FloatingInput";
import Select from "../common/Select";
import ClientSearch from "../common/ClientSearch";
import {
  censusBillingRules,
  getCensusPayors,
  censusGroupPlanAction,
  censusServiceConfigs,
} from "../services/soar";

function NewClientCensusRuleModal({ isOpen, setIsOpen }) {
  const [setupData, setSetupData] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [client, setClient] = useState(null);
  const [payor, setPayor] = useState(null);
  const [groupPlan, setGroupPlan] = useState(null);
  const [serviceConfiguration, setServiceConfiguration] = useState(null);
  const applicationId =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
      : "58990f83-8566-4ba8-b4b5-584a64e4cba1";
  const navigate = useNavigate();

  useEffect(() => {
    getSetupData();
  }, []);

  const getSetupData = async () => {
    const payorResponse = await getCensusPayors();
    const groupPlansResponse = await censusGroupPlanAction({
      command: "query",
      keyword: "viewall",
      application: applicationId,
    });
    const serviceConfigResponse = await censusServiceConfigs({
      command: "query",
      application: applicationId,
      keyword: "viewall",
    });
    setSetupData({
      ...setupData,
      payors: payorResponse
        .filter((payor) => payor.active)
        .map((payor) => ({
          _id: payor.id,
          name: payor.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      groupPlans: groupPlansResponse.data
        .filter((groupPlan) => groupPlan.active)
        .map((groupPlan) => ({
          _id: groupPlan.id,
          name: groupPlan.name,
          payorId: groupPlan.payorId,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      serviceConfigurations: serviceConfigResponse.data
        .filter((serviceConfig) => serviceConfig.active)
        .map((serviceConfig) => ({
          _id: serviceConfig.id,
          name: serviceConfig.name,
          config: serviceConfig.configuration,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    });
  };

  const handleSave = async () => {
    const body = {
      application: applicationId,
      command: "insert",
      type: "Census",
      name: `${client.fullname} SPECIFIC RULE`,
      payor: payor,
      groupPlan: groupPlan,
      serviceConfiguration: serviceConfiguration,
      client: client.contactid,
      clientName: client.fullname,
      configuration: {},
    };

    const response = await censusBillingRules(body);

    if (response.code === "success") {
      toast.success("Rule saved successfully!");
      handleClose();
      navigate(`/rev/rules/${response.id}`);
    } else {
      toast.error(`Error saving rule: ${response.message}`);
    }
  };

  const handleClose = () => {
    setClient(null);
    setPayor(null);
    setGroupPlan(null);
    setServiceConfiguration(null); // Updated to setServiceConfiguration(null)
    setIsOpen(false);
  };

  return (
    <Modal show={isOpen} onHide={handleClose} animation={false} size="xl">
      <Modal.Header closeButton className="bg-safy-info text-white">
        <Modal.Title>Group Plan</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!client ? (
          <>
            <Select
              name="clientSearchState"
              label="Select State"
              value={selectedState}
              options={[
                { _id: "SAFY of Alabama", name: "Alabama" },
                { _id: "SAFY of Colorado", name: "Colorado" },
                { _id: "SAFY of Indiana", name: "Indiana" },
                { _id: "SAFY of Kentucky", name: "Kentucky" },
                { _id: "SAFY of Nevada", name: "Nevada" },
                { _id: "SAFY of Ohio", name: "Ohio" },
                { _id: "SAFY of South Carolina", name: "South Carolina" },
              ]}
              onChange={(e) => setSelectedState(e.currentTarget.value)}
            />

            {selectedState !== "" && (
              <>
                <div className="h4 my-4">Contact Search</div>
                <ClientSearch
                  state={selectedState}
                  show={selectedState !== ""}
                  setShow={() => setSelectedState("")}
                  setClient={(e) => setClient(e)}
                  hideNewClient={true}
                />
              </>
            )}
          </>
        ) : (
          <>
            <div className="h4 mb-4">
              {`Client: ${client.lastname}, ${client.firstname}`}
              <i
                className="fas fa-circle-xmark text-danger ms-4"
                role="button"
                onClick={() => setClient(null)}
              />
            </div>
            <div className="mt-4">
              <FloatingInput
                type="select"
                name="payor"
                label="Payor"
                options={setupData.payors}
                value={payor}
                onChange={(e) => setPayor(e.target.value)}
              />
            </div>
            {payor && (
              <div className="mt-4">
                <FloatingInput
                  type="select"
                  name="groupPlan"
                  label="Group Plan"
                  options={setupData.groupPlans.filter(
                    (gp) => gp.payorId === payor
                  )}
                  value={groupPlan}
                  onChange={(e) => setGroupPlan(e.target.value)}
                />
              </div>
            )}
            {groupPlan && (
              <div className="mt-4">
                <FloatingInput
                  type="select"
                  name="serviceConfiguration"
                  label="Service Configuration"
                  options={setupData.serviceConfigurations}
                  value={serviceConfiguration}
                  onChange={(e) => setServiceConfiguration(e.target.value)}
                />
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div
          className="btn btn-success"
          onClick={() => handleSave()}
          disabled={!serviceConfiguration}
        >
          Save
        </div>
        <div className="btn btn-danger" onClick={handleClose}>
          Close
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default NewClientCensusRuleModal;
